import React from 'react'
import styled from '@emotion/styled'
import { Calendar, ArrowRight, X } from 'lucide-react'

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${(props: any) => props.theme.colors?.white || '#ffffff'};
  border: 1px solid
    ${(props: any) => props.theme.colors?.borderColor || '#e2e8f0'};
  border-radius: 10px;
  height: 40px;
  padding: 0 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease-in-out;
  cursor: text;

  &:hover {
    border-color: ${(props: any) => props.theme.colors?.blue500 || '#004aad'};
  }

  &:focus-within {
    border-color: ${(props: any) => props.theme.colors?.blue500 || '#004aad'};
    box-shadow: 0 0 0 3px rgba(0, 74, 173, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    overflow-x: auto;

    /* Hide scrollbar for clean look */
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
`

const IconButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  margin-right: 10px;

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 2;
  }
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  margin: 0 10px;

  svg {
    width: 14px;
    height: 14px;
  }
`

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`

const StyledInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  color: ${(props: any) => props.theme.colors?.text || '#334155'};
  font-size: 14px;
  font-weight: 500;
  width: 120px;
  font-family: inherit;
  letter-spacing: 0.3px;
  text-align: center;

  /* Style the native date picker parts */
  ::-webkit-datetime-edit {
    padding: 0;
  }

  ::-webkit-datetime-edit-fields-wrapper {
    padding: 0;
  }

  ::-webkit-datetime-edit-text {
    color: ${(props: any) => props.theme.colors?.placeholder || '#94a3b8'};
    padding: 0 0.1em;
  }

  ::-webkit-datetime-edit-month-field,
  ::-webkit-datetime-edit-day-field,
  ::-webkit-datetime-edit-year-field {
    color: ${(props: any) => props.theme.colors?.text || '#334155'};
    &:focus {
      background-color: ${(props: any) =>
        props.theme.colors?.borderColor || '#f1f5f9'};
      color: ${(props: any) => props.theme.colors?.text || '#0f172a'};
      border-radius: 4px;
    }
  }

  /* Make the native calendar icon subtle but clickable */
  ::-webkit-calendar-picker-indicator {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    background: transparent;
  }
`

const ClearButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  margin-left: 8px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background-color: ${(props: any) =>
      props.theme.colors?.borderColor || '#f1f5f9'};
    color: ${(props: any) => props.theme.colors?.red || '#ef4444'};
  }

  svg {
    width: 14px;
    height: 14px;
    stroke-width: 2.5;
  }
`

export interface DateRangeFilterProps {
  fromDate?: string
  toDate?: string
  onFromDateChange?: (date: string) => void
  onToDateChange?: (date: string) => void
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
}) => {
  return (
    <FilterContainer>
      <IconButton>
        <Calendar />
      </IconButton>
      <InputWrapper>
        <StyledInput
          type="date"
          value={fromDate || ''}
          onChange={(e) => onFromDateChange?.(e.target.value)}
        />
      </InputWrapper>
      <Divider>
        <ArrowRight />
      </Divider>
      <InputWrapper>
        <StyledInput
          type="date"
          value={toDate || ''}
          onChange={(e) => onToDateChange?.(e.target.value)}
        />
      </InputWrapper>
      {(fromDate || toDate) && (
        <ClearButton
          onClick={() => {
            onFromDateChange?.('')
            onToDateChange?.('')
          }}
          title="Clear dates"
        >
          <X />
        </ClearButton>
      )}
    </FilterContainer>
  )
}

export default DateRangeFilter
